import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const URL = "https://coastsidelaw.com.au/business-succession-planning";

const FAQS = [
  {
    q: "What is business succession planning?",
    a: "It's a written plan for what happens to your business if you retire, sell, fall seriously ill or die. It usually combines your will, enduring power of attorney, company or trust documents and an agreement with your co-owners, so they all work together.",
  },
  {
    q: "Does my will cover my business?",
    a: "Often not fully. Shares in a company can pass under your will, but assets held in a family trust don't. Control of a trust passes under the trust deed, so the deed, the company constitution and your will all need to line up.",
  },
  {
    q: "What happens if a business partner dies without a plan?",
    a: "Their shares or interest usually pass to their estate, which can leave you in business with their family. A buy-sell agreement, often funded by insurance, lets the surviving owners buy that interest at an agreed value.",
  },
  {
    q: "Who should have an enduring power of attorney for business matters?",
    a: "Someone who can run things if you lose capacity. In Queensland, an enduring power of attorney can cover financial matters, including business decisions. Company directorships need separate planning, because an attorney can't simply step in as a director.",
  },
  {
    q: "How much does a succession plan cost?",
    a: "It depends on the structure. We look at your company, trust and partnership documents first, then give you a fixed quote before any work starts.",
  },
  {
    q: "Do you work with my accountant?",
    a: "Yes. Tax and valuation questions belong with your accountant, and we prefer to work alongside them so the legal documents match the financial plan.",
  },
];

export const Route = createFileRoute("/business-succession-planning")({
  head: () => ({
    meta: [
      { title: fitTitle("Business Succession Planning Lawyers QLD | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Business succession planning in Queensland: wills, trusts, buy-sell agreements and powers of attorney that work together. Offices at Golden Beach and Scarborough.",
        ),
      },
      { property: "og:title", content: "Business succession planning in Queensland" },
      { property: "og:description", content: "Make sure your business is protected if you retire, sell, fall ill or die." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Business · Succession"
      title="Business succession planning"
      lead="A business succession plan sets out who takes over your business, and on what terms, if you retire, sell, lose capacity or die. Without one, your shares or trust can end up with people who never meant to run the business. We make sure your will, trust deed, company documents and co-owner agreements all work together. Offices at Golden Beach and Scarborough, acting across Queensland."
      ctaLabel="Talk to us about succession"
      boxTitle="What a succession plan usually covers"
      boxItems={[
        "A will that deals properly with company shares and business assets",
        "Who controls your family trust, and who takes over as appointor",
        "Buy-sell agreements between co-owners, often backed by insurance",
        "Enduring power of attorney for financial and business decisions",
        "Checking your company constitution and partnership agreements",
        "Working with your accountant on tax and valuation",
      ]}
      faqs={FAQS}
      links={[
        { to: "/shareholder-agreements", label: "Shareholder agreements" },
        { to: "/business-sales", label: "Buying & selling a business" },
        { to: "/testamentary-trusts", label: "Testamentary trusts" },
        { to: "/wills-and-estates", label: "Wills and estates" },
      ]}
      formHeading="Ask about succession planning"
      ctaTitle="Protect the business you've built"
      ctaBody={`Call ${FIRM.phone}. We'll review your current set-up and give you a fixed quote.`}
    />
  ),
});
